/**
 * Legal obligations a contracting company ("contratante") protects by collecting
 * its specialized-service suppliers' documents. The compliance dashboard groups
 * document definitions under these obligations so a gap reads as a concrete
 * consequence (e.g. a payment that is not deductible) rather than a bare
 * percentage.
 *
 * Informational reference only; it is not legal advice. Definitions are mapped
 * by name so tenants that rename a definition can still be classified; a
 * definition that matches nothing falls under "otros".
 */
export type ObligationId = 'repse' | 'isr' | 'iva' | 'opiniones' | 'seguridad_social' | 'expediente' | 'otros';

export interface Obligation {
  id: ObligationId;
  /** Short column label. */
  short: string;
  title: string;
  /** What is at stake for the contratante, in one sentence. */
  consequence: string;
  legalBasis: string;
  /** Relative weight in the supplier risk score. */
  weight: number;
}

export const OBLIGATIONS: Obligation[] = [
  {
    id: 'isr',
    short: 'ISR',
    title: 'Deducción de ISR',
    consequence: 'Sin CFDI de nómina, declaración y pago de retenciones y pago de cuotas IMSS, los pagos al proveedor no son deducibles.',
    legalBasis: 'LISR art. 27 fr. V · CFF art. 15-D',
    weight: 4,
  },
  {
    id: 'iva',
    short: 'IVA',
    title: 'Acreditamiento de IVA',
    consequence: 'La declaración y el acuse de pago de IVA del proveedor deben obtenerse a más tardar el último día del mes siguiente al pago; si no, el IVA no es acreditable.',
    legalBasis: 'LIVA art. 5 fr. II · CFF art. 15-D',
    weight: 4,
  },
  {
    id: 'repse',
    short: 'REPSE',
    title: 'Registro REPSE e informativas',
    consequence: 'Contratar a un proveedor sin registro vigente es subcontratación prohibida; omitir las informativas cuatrimestrales (ICSOE/SISUB) pone en riesgo su registro.',
    legalBasis: 'LFT arts. 13, 15 y 1004-C · LSS art. 15-A',
    weight: 5,
  },
  {
    id: 'opiniones',
    short: 'Opiniones',
    title: 'Opiniones de cumplimiento',
    consequence: 'Opiniones positivas de SAT, IMSS e INFONAVIT respaldan que el proveedor está al corriente; la del SAT tiene vigencia de 30 días.',
    legalBasis: 'CFF art. 32-D · RMF regla 2.1.37',
    weight: 3,
  },
  {
    id: 'seguridad_social',
    short: 'Seg. social',
    title: 'Seguridad social y obligaciones laborales',
    consequence: 'El contratante responde solidariamente por las obligaciones del proveedor con los trabajadores que presta.',
    legalBasis: 'LFT art. 13 · CFF art. 26 fr. XVI',
    weight: 3,
  },
  {
    id: 'expediente',
    short: 'Expediente',
    title: 'Expediente legal del proveedor',
    consequence: 'Contrato, representación legal y datos fiscales vigentes sustentan la relación y la materialidad del servicio.',
    legalBasis: 'LFT art. 15-A · CFF art. 15-D',
    weight: 1,
  },
  {
    id: 'otros',
    short: 'Otros',
    title: 'Otros documentos',
    consequence: 'Documentos solicitados por tu organización que no están clasificados en una obligación.',
    legalBasis: '—',
    weight: 1,
  },
];

export const OBLIGATION_BY_ID = Object.fromEntries(OBLIGATIONS.map((o) => [o.id, o])) as Record<ObligationId, Obligation>;

const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9.]+/g, ' ')
    .trim();

// Ordered rules; a definition can support more than one obligation
// (e.g. the SAT payment acuse backs both ISR withholding and IVA).
const RULES: Array<{ test: RegExp; obligations: ObligationId[] }> = [
  { test: /\bopinion\b/, obligations: ['opiniones'] },
  { test: /registro repse|\brepse\b/, obligations: ['repse'] },
  { test: /\bicsoe\b|\bsisub\b/, obligations: ['repse'] },
  { test: /cfdi s? de nomina|\bnomina\b/, obligations: ['isr'] },
  { test: /isr retenciones|retenciones/, obligations: ['isr'] },
  { test: /acuse de recibo del pago sat|pago sat/, obligations: ['isr', 'iva'] },
  { test: /declaracion iva|\biva\b/, obligations: ['iva'] },
  { test: /linea de captura y pago imss|pago imss|cfdi s? imss/, obligations: ['isr', 'seguridad_social'] },
  { test: /\bimss\b|\bsua\b|\bema\b|\beba\b|infonavit|fonacot|no adeudo|asistencia|antiguedad|registro patronal/, obligations: ['seguridad_social'] },
  { test: /\bisn\b/, obligations: ['seguridad_social'] },
  { test: /contrato|identificacion|poder notarial|domicilio|declaracion anual|constancia de situacion fiscal/, obligations: ['expediente'] },
];

export function obligationsForDefinition(name: string | null | undefined): ObligationId[] {
  const value = normalize(name || '');
  const rule = RULES.find((r) => r.test.test(value));
  return rule ? rule.obligations : ['otros'];
}
